import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { fmt } from '../data/metrics';
import type {
  RecoveryData,
  RecoveryOpportunity,
  RecoveryPattern,
} from '../types';
import { Icon } from './shared/Icon';

/** Short type line under the outlet name (noun fragments). */
function oppTypeLine(o: RecoveryOpportunity): string {
  switch (o.type) {
    case 'recoverable':
      return i18n.t('live:recovery.tablesOpen', { count: o.count ?? 0 });
    case 'noshow':
      return i18n.t('live:recovery.noshow');
    case 'waitlist':
      return i18n.t('live:recovery.waitlistParty', { count: o.count ?? 0 });
    case 'surge':
      return i18n.t('live:recovery.surge');
    case 'largeparty':
      return i18n.t('live:recovery.largeparty');
    default:
      return '';
  }
}

const LEVER_KEYS: Record<string, string> = {
  refill: 'live:recovery.refill',
  waitlist: 'live:recovery.waitlist',
  event: 'live:recovery.event',
  reconfirm: 'live:recovery.reconfirm',
};

function valFor(rec: RecoveryData, eur: number, cov: number): string {
  return rec.hasPos ? fmt.eur(eur) : fmt.num(cov);
}

function oppVal(rec: RecoveryData, f: RecoveryOpportunity): string {
  if (rec.hasPos) {
    return i18n.t('live:recovery.moneyGuests', {
      value: fmt.eur(f.eur),
      count: fmt.num(f.cov),
    });
  }
  return fmt.num(f.cov);
}

function CaptureChip({ rec }: { rec: RecoveryData }) {
  if (rec.captureRate == null) {
    return (
      <div
        className="rec-chip neutral"
        title={i18n.t('live:recovery.rateTip')}
      >
        <Icon name="target" size={12} stroke={2.3} />
        {i18n.t('live:recovery.noRebooks')}
      </div>
    );
  }
  const base = rec.baselineCaptureRate;
  const delta = base != null ? rec.captureRate - base : null;
  const up = delta != null && delta >= 0;
  const tip =
    base != null
      ? `${i18n.t('live:recovery.usual', { rate: base })}${rec.regimeLabel ? ` · ${rec.regimeLabel}` : ''}`
      : i18n.t('live:recovery.rebookedCancels');
  return (
    <div
      className={`rec-chip${delta == null ? ' neutral' : up ? ' up' : ' down'}`}
      title={tip}
    >
      <Icon
        name={delta == null ? 'target' : up ? 'arrowUp' : 'arrowDown'}
        size={12}
        stroke={2.4}
      />
      <b>{rec.captureRate}%</b>
      <span> {i18n.t('live:recovery.rebooked')}</span>
    </div>
  );
}

function Tallies({ rec }: { rec: RecoveryData }) {
  return (
    <div className="rec-tallies">
      <div
        className="rec-tally recovered"
        title={i18n.t('live:recovery.sameTableTip')}
      >
        <div className="rt-v">
          {valFor(rec, rec.recoveredEur, rec.recoveredCov)}
          {!rec.hasPos && <span className="rt-u">{i18n.t('live:recovery.guests')}</span>}
        </div>
        <div className="rt-l">
          <Icon name="check" size={12} stroke={2.6} />
          {i18n.t('live:recovery.recovered', { count: rec.recoveredCount })}
        </div>
      </div>
      <div
        className="rec-tally recoverable"
        title={i18n.t('live:recovery.freeSlotTip')}
      >
        <div className="rt-v">
          {valFor(rec, rec.recoverableEur, rec.recoverableCov)}
          {!rec.hasPos && <span className="rt-u">{i18n.t('live:recovery.guests')}</span>}
        </div>
        <div className="rt-l">
          <Icon name="target" size={12} stroke={2.4} />
          {i18n.t('live:recovery.stillOpen', { count: rec.recoverableCount })}
        </div>
      </div>
      <div
        className="rec-tally lost"
        title={i18n.t('live:recovery.slotPassedTip')}
      >
        <div className="rt-v">
          {valFor(rec, rec.lostEur, rec.lostCov)}
          {!rec.hasPos && <span className="rt-u">{i18n.t('live:recovery.guests')}</span>}
        </div>
        <div className="rt-l">
          <Icon name="arrowDown" size={12} stroke={2.4} />
          {i18n.t('live:recovery.lost', { count: rec.lostCount })}
        </div>
      </div>
      <div
        className="rec-tally walkin"
        title={i18n.t('live:recovery.walkinsTip')}
      >
        <div className="rt-v">
          {valFor(rec, rec.walkInEur, rec.walkInCov)}
          {!rec.hasPos && <span className="rt-u">{i18n.t('live:recovery.guests')}</span>}
        </div>
        <div className="rt-l">
          <Icon name="users" size={12} stroke={2.4} />
          {i18n.t('live:recovery.walkinsCount', { count: rec.walkInCount })}
        </div>
      </div>
    </div>
  );
}

function MoneyClock({
  rec,
  expanded,
}: {
  rec: RecoveryData;
  expanded: boolean;
}) {
  const outletCount = new Set(rec.opportunities.map(o => o.outletId)).size;
  const chances = rec.opportunities.length;
  return (
    <div className={`rec-clock${expanded ? '' : ' is-collapsed'}`}>
      <div className="rec-now">
        <div
          className="rec-now-lab"
          title={i18n.t('live:recovery.openTip')}
        >
          <span className="rec-live-dot" />
          {i18n.t('live:recovery.now')}
        </div>
        <div className="rec-now-val">
          {valFor(rec, rec.onTableEur, rec.onTableCov)}
          <span className="rec-now-unit">
            {rec.hasPos
              ? i18n.t('live:recovery.recoverableUnit')
              : i18n.t('live:recovery.guests')}
          </span>
        </div>
        <div className="rec-now-sub">
          {i18n.t('live:recovery.chance', { count: chances })} ·{' '}
          {i18n.t('live:recovery.outlet', { count: outletCount })}
        </div>
        {expanded ? <CaptureChip rec={rec} /> : null}
      </div>
      {expanded ? <Tallies rec={rec} /> : null}
    </div>
  );
}

function opportunityValue(rec: RecoveryData, o: RecoveryOpportunity): number {
  return rec.hasPos ? o.eur : o.cov;
}

function leverLabel(f: RecoveryOpportunity): string {
  const key = LEVER_KEYS[f.lever.id];
  return key ? i18n.t(key) : f.lever.label;
}

function OpportunityList({
  rec,
  onFocus,
}: {
  rec: RecoveryData;
  onFocus: (outletId: string) => void;
}) {
  const opportunities = [...rec.opportunities].sort(
    (a, b) => opportunityValue(rec, b) - opportunityValue(rec, a),
  );
  const outletCount = new Set(opportunities.map(o => o.outletId)).size;

  return (
    <div className="ol-card rec-feed rec-feed-expanded">
      <div className="ol-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="activity" size={16} style={{ color: 'var(--accent)' }} />
          <h3>{i18n.t('live:recovery.where')}</h3>
        </div>
        <span className="ol-conf">
          <span className="rec-live-dot" style={{ width: 6, height: 6 }} />
          {i18n.t('live:recovery.chance', { count: opportunities.length })} ·{' '}
          {i18n.t('live:recovery.outlet', { count: outletCount })}
        </span>
      </div>
      {opportunities.length === 0 ? (
        <div className="rec-clear">
          <Icon
            name="check"
            size={20}
            stroke={2.2}
            style={{ color: 'var(--st-steady)' }}
          />
          {i18n.t('live:recovery.allClear')}
        </div>
      ) : (
        <div className="rec-list rec-list-expanded">
          {opportunities.map(f => (
            <div
              className="rec-row feed"
              key={f.id}
              onClick={() => onFocus(f.outletId)}
            >
              <div className="rec-row-ic" style={{ color: 'var(--accent)' }}>
                <Icon name={f.lever.icon} size={15} stroke={2.4} />
              </div>
              <div className="rec-row-main">
                <div className="rec-row-top">
                  <b>{f.name}</b>
                  <span className="rec-city">{f.city}</span>
                </div>
                <div
                  className="rec-row-act"
                  title={f.why || undefined}
                >
                  {oppTypeLine(f)}
                </div>
                <div
                  className="rec-lever"
                  title={[f.why, f.lever.how].filter(Boolean).join(' · ') || undefined}
                >
                  <Icon name="bolt" size={11} fill />
                  {leverLabel(f)}
                </div>
              </div>
              <div className="rec-row-val">{oppVal(rec, f)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PatternRich({ text }: { text: string }) {
  const parts = String(text).split('**');
  return (
    <>
      {parts.map((p, i) =>
        i % 2 ? <b key={i}>{p}</b> : <React.Fragment key={i}>{p}</React.Fragment>,
      )}
    </>
  );
}

function PatternStrip({ patterns }: { patterns: RecoveryPattern[] }) {
  if (!patterns.length) {
    return null;
  }
  const tone: Record<RecoveryPattern['tone'], string> = {
    alert: 'var(--st-alert)',
    win: 'var(--st-steady)',
    info: 'var(--petrol-ink)',
  };
  return (
    <div className="rec-patterns">
      {patterns.map((p, i) => (
        <div
          className="rec-pat"
          key={i}
          style={{ borderLeftColor: tone[p.tone] }}
        >
          <div className="rec-pat-head">
            <Icon name={p.icon} size={14} style={{ color: tone[p.tone] }} />
            <span className="rec-pat-badge">{i18n.t('live:recovery.note')}</span>
          </div>
          <div className="rec-pat-text">
            <PatternRich text={p.text} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function RecoverySection({
  recovery,
  onFocus,
}: {
  recovery: RecoveryData | null;
  onFocus?: (outletId: string) => void;
}) {
  const { t } = useTranslation('live');
  const [expanded, setExpanded] = useState(false);

  if (!recovery) {
    return (
      <section className="recovery" data-ftue="live-recovery">
        <div className="sec-head">
          <h2>{t('recovery.title')}</h2>
        </div>
        <p className="loading-text">{t('recovery.loading')}</p>
      </section>
    );
  }

  return (
    <section className="recovery" data-ftue="live-recovery">
      <div className="sec-head">
        <h2>{t('recovery.title')}</h2>
        <span className="sh-meta">{t('recovery.meta')}</span>
        <span
          className="prov-note"
          title={t('recovery.dataTip')}
        >
          {t('recovery.estimated')}
        </span>
        <button
          type="button"
          className="rec-details-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? t('recovery.hide') : t('recovery.details')}
        </button>
      </div>
      <MoneyClock rec={recovery} expanded={expanded} />
      {expanded ? (
        <>
          <PatternStrip patterns={recovery.patterns} />
          {onFocus ? <OpportunityList rec={recovery} onFocus={onFocus} /> : null}
        </>
      ) : null}
    </section>
  );
}
